import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import {
  type ThreadPreviewsCompPayload,
  transformThreadPreviewToThread,
  isThreadPreviewComp,
} from '../payloads/thread-previews'
import type {Thread, ThreadPositioning} from '@github-ui/diff-lines/conversation-types'
import {fileLevelCommentKey, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import {useGetThreadFromThreadID, useMarkersActions} from '../../stores/selectors'
import {OUTSIDE_DIFF_KEY} from '../../helpers/diff-helpers'

export function threadPreviewsQueryKey(pathName: string): QueryKey {
  return [PageData.threadPreviews, pathName]
}

export type ThreadPreviewsPage = {
  data: Thread[]
  nextUrl: string | null
}

export function threadPreviewsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.threadPreviews}`
}
/**
 *
 * @param thread
 * @returns the line key and file key while taking into account whether or not the thread is unpositionable. If
 *  it is, we return OUTSIDE_DIFF_KEY
 */
export function getThreadPreviewsLineAndFileKeysFromThread(thread: Thread): {
  filePath: string
  lineKey: LineKey | FileKey
} {
  if (!thread.shouldRenderInDiffLines) {
    return {filePath: OUTSIDE_DIFF_KEY, lineKey: OUTSIDE_DIFF_KEY}
  }

  const markerEndLine = thread.positioning?.endLine !== undefined ? thread.positioning.endLine : thread.subject?.endLine
  const markerEndDiffSide = thread.positioning?.endDiffSide ?? thread.subject?.endDiffSide
  const isRightOrLeftSide = markerEndDiffSide === 'RIGHT' ? 'R' : 'L'
  // if the thread has no end line, then it is a file level comment.
  const isFileLevelComment = thread.subjectType?.toUpperCase() === 'FILE'

  const threadPath = thread.positioning?.path ?? thread.path ?? ''
  // we stitch the path into an unpositioned comment, but if it has no end line, then we know for sure that
  // it is unpositionable.
  const filePath = isFileLevelComment ? threadPath : markerEndLine && threadPath !== '' ? threadPath : OUTSIDE_DIFF_KEY
  const backupLineKey = isFileLevelComment ? fileLevelCommentKey : OUTSIDE_DIFF_KEY
  const lineKey = markerEndLine ? (`${isRightOrLeftSide}${markerEndLine}` as LineKey) : backupLineKey

  return {filePath, lineKey}
}

/**
 * Hook for fetching thread previews with pagination support.
 * Returns the infinite query result directly, allowing consumers to manage pagination.
 *
 * The API will only return a link to the next page if there are more pages to fetch and the feature flag is enabled.
 *
 * @param pathName - The base path for the API request
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteThreadPreviewsPageData({
  pathName,
}: {
  pathName: string
}): UseSuspenseInfiniteQueryResult<InfiniteData<ThreadPreviewsPage>> {
  const getThreadFromStore = useGetThreadFromThreadID()
  const {updateThread} = useMarkersActions()
  return useSuspenseInfiniteQuery<ThreadPreviewsPage, Error, InfiniteData<ThreadPreviewsPage>, QueryKey, string>({
    queryKey: threadPreviewsQueryKey(pathName),
    initialPageParam: threadPreviewsApiUrl(pathName),
    queryFn: async ({pageParam}: {pageParam: string}): Promise<ThreadPreviewsPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch thread previews from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')

      // Handle multiple backend formats for backward/forward compatibility:
      // 1. Empty arrays (no transformation needed)
      // 2. ThreadPreviewsCompPayload (old format with previewInfo - needs transformation)
      // 3. Thread[] with commentsData (new format - already correct structure)
      let data: Thread[] = []
      if (json.length > 0) {
        // Check if this is the old format (ThreadPreviewComp with previewInfo properties)
        if (isThreadPreviewComp(json[0])) {
          data = (json as ThreadPreviewsCompPayload).map(transformThreadPreviewToThread)
        } else {
          // New format Thread[] with commentsData already present
          data = json as Thread[]
        }
      }

      for (const thread of data) {
        const filePath = thread.path ?? ''
        const isRightOrLeftSide = thread.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'
        const lineKey = thread.subject?.originalEndLine
          ? (`${isRightOrLeftSide}${thread.subject.originalEndLine}` as LineKey)
          : fileLevelCommentKey

        const storedThread = getThreadFromStore(Number(thread.id))
        if (!storedThread) {
          //if the thread isn't in the store, we need to add it.
          updateThread(Number(thread.id), filePath, lineKey, () => {
            return {...thread, shouldRenderInDiffLines: false}
          })
        } else {
          // if the stored thread has no positioning, that means it was outside of the diff and we could not
          // find a position for it. So when we update the data for that thread, we want ot use the outside of
          // the diff key. In the new positioning, however, we still want to use the filePath that came back
          // from the thread itself.
          const {filePath: storedThreadFilePath, lineKey: storedThreadLineKey} =
            getThreadPreviewsLineAndFileKeysFromThread(storedThread)
          const newPositioning: ThreadPositioning = {
            endLine: thread.subject?.endLine,
            endDiffSide: thread.subject?.endDiffSide,
            isOutdated: thread.isOutdated || false,
            path: filePath,
            startDiffSide: thread.subject?.startDiffSide,
            startLine: thread.subject?.startLine,
          }
          updateThread(Number(thread.id), storedThreadFilePath, storedThreadLineKey, () => {
            return {
              ...storedThread,
              positioning: newPositioning,
              subject: {...storedThread.subject, diffLines: thread.subject?.diffLines},
            }
          })
        }
      }

      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: ThreadPreviewsPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
  })
}
